import React, { useContext } from 'react';
import { Popover, StatusIndicator } from '@amzn/awsui-components-react/polaris';
import DateUtils from 'src/utils/dateUtils';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import { ColumnDef, ColumnPullStatus, PullStatusResponse } from 'src/models/segment-cdt/SegmentCdtTypes';

/** Wire status -> the indicator type and the word shown beside it. */
const PRESENTATION: Record<string, { type: 'success' | 'error' | 'in-progress' | 'pending', label: string }> = {
    [ColumnPullStatus.COMPLETED]: { type: 'success', label: 'Completed' },
    [ColumnPullStatus.FAILED]: { type: 'error', label: 'Failed' },
    [ColumnPullStatus.IN_PROGRESS]: { type: 'in-progress', label: 'In progress' },
    [ColumnPullStatus.PENDING]: { type: 'pending', label: 'Queued' },
};

// `DateUtils.formatTimestamp` rather than `FormattingService`'s: it renders in America/Los_Angeles with the
// zone spelled out (`2026/09/09 11:12 PM PDT`), which is what MEC's "GL data ingestion timestamp" and this
// feature's own validation modal and list grid already do. FormattingService formats in the browser's local
// zone and labels nothing, so the same instant read as a different day either side of a timezone boundary.
// Both take epoch milliseconds, which is what every timestamp here is.

/**
 * When the pull finished, shown only for a terminal status.
 *
 * `completedAt` is null until terminal, so there is nothing to show while a pull runs. On the rare terminal
 * status with no `completedAt`, nothing is shown rather than falling back to `startedAt`: labelling a start
 * time as a finish time would be worse than omitting it.
 */
function completionTime(pullStatus: PullStatusResponse): string {
    const terminal = pullStatus.overallStatus === ColumnPullStatus.COMPLETED
        || pullStatus.overallStatus === ColumnPullStatus.FAILED;
    if (!terminal || pullStatus.completedAt == null) {
        return '';
    }
    return DateUtils.formatTimestamp(pullStatus.completedAt);
}

/**
 * What the metadata row says once no execution is being tracked — which is every reload, and any period
 * nobody has pulled in this session.
 *
 * <p>Reports the most specific timestamp available and nothing beyond it:
 *
 * <ul>
 *   <li><b>a pull timestamp</b> — "GL Pull: Last pulled &lt;when&gt;". A row carries one, so a pull did write
 *       to this period. No verdict, because none is stored (see the component docstring).
 *   <li><b>only a data timestamp</b> — "Data last updated &lt;when&gt;", under its own label, because an
 *       upload moves it too. Deliberately not shown under "GL Pull", which it would misattribute.
 *   <li><b>neither</b> — nothing. Absence of a timestamp is not evidence that nothing was pulled: the only
 *       durable trace of a pull is written alongside a value, so a pull that produced no values leaves none.
 *       Saying "not pulled" would be a claim this cannot support.
 * </ul>
 */
function DurablePullState(props: { lastPulledAt?: number; dataUpdatedAt?: number }) {
    const { lastPulledAt, dataUpdatedAt } = props;
    if (lastPulledAt != null) {
        return (
            <span>
                <strong>GL Pull:</strong>{' '}Last pulled{' '}
                <span className="segmentPullStatusTime">{DateUtils.formatTimestamp(lastPulledAt)}</span>
            </span>
        );
    }
    if (dataUpdatedAt != null) {
        return (
            <span>
                <strong>Data last updated:</strong>{' '}
                <span className="segmentPullStatusTime">{DateUtils.formatTimestamp(dataUpdatedAt)}</span>
            </span>
        );
    }
    return null;
}

/** A column's display name, falling back to its id (which is what the status map is keyed by). */
function columnLabel(columnId: string, columns: ColumnDef[]): string {
    const match = (columns ?? []).find((c) => c.columnId === columnId);
    return match?.displayName ?? columnId;
}

/**
 * The GL pull's state for the selected period, in the detail view's metadata row.
 *
 * <p>Three sources, most informative first, and each says strictly less than the one above it:
 *
 * <ol>
 *   <li><b>The tracked execution</b> (`pullStatus`) — the overall status as a word, with the per-column
 *       detail on a popover. That per-column map is the substance: `configStatuses` and `errors` are
 *       maintained per column by the pull workflow, and the failure banner tells the user to "check the
 *       column pull statuses". It is readable only while an execution record lives, so after a reload it is
 *       gone — which is the whole reason the two fallbacks exist.
 *   <li><b>When the period was last pulled</b> (`lastPulledAt`) — the newest per-column pull timestamp
 *       across its rows, from `GetSegmentAggregates.lastUpdated`. Stored on the rows, so it survives a
 *       reload; says only <i>when</i>, never whether the pull succeeded.
 *   <li><b>When the period's data last changed</b> (`dataUpdatedAt`) — from `GetSegmentPeriods`. Not
 *       pull-specific (an upload moves it too), so it is labelled as what it is and makes no claim about a
 *       pull at all.
 * </ol>
 *
 * <p><b>Nothing here ever infers a verdict.</b> No durable record says whether the last pull succeeded, so
 * once the execution record is gone `COMPLETED` and `FAILED` are indistinguishable from here and a success
 * tick beside a timestamp would be invented. For the same reason there is no "not pulled" wording: absence
 * of a timestamp is not evidence a pull never ran, so this renders nothing rather than claiming it.
 */
export default function SegmentPullStatusIndicator() {
    const context = useContext(SegmentTablesContext);
    const detail = context?.state?.detail;
    const pullStatus = detail?.pullStatus;
    const columns = detail?.definition?.columns ?? [];
    const lastPulledAt = detail?.aggregates?.lastUpdated;
    // The selected period's own entry. Its `lastUpdated` is the newest row change in that period, which is
    // durable — unlike the execution record — and so is the last thing this falls back to.
    const dataUpdatedAt = (detail?.periods ?? []).find((p) => p.period === detail?.selectedPeriod)?.lastUpdated;
    // No pull tracked, or one whose overall status the server did not report (the mapper omits the status
    // rather than defaulting it when the record carries none). Fall back to what the rows themselves record.
    if (!pullStatus?.overallStatus) {
        return <DurablePullState lastPulledAt={lastPulledAt} dataUpdatedAt={dataUpdatedAt} />;
    }

    const overall = PRESENTATION[pullStatus.overallStatus];
    if (!overall) {
        // A status outside the declared vocabulary: report it verbatim rather than guessing a category,
        // so a contract addition shows up as itself instead of being silently recategorised.
        return (
            <span>
                <strong>GL Pull:</strong>{' '}
                <StatusIndicator type="info">{String(pullStatus.overallStatus)}</StatusIndicator>
            </span>
        );
    }

    const configStatuses = pullStatus.configStatuses ?? {};
    const errors = pullStatus.errors ?? {};
    const columnIds = Object.keys(configStatuses);

    const indicator = <StatusIndicator type={overall.type}>{overall.label}</StatusIndicator>;
    const finishedAt = completionTime(pullStatus);
    const timestamp = finishedAt
        ? <span className="segmentPullStatusTime">{finishedAt}</span>
        : null;

    // Nothing per-column to drill into (a pull that has not reached the per-column stage, or a table with
    // no GL columns), so the word stands alone rather than offering an empty popover.
    if (!columnIds.length) {
        return <span><strong>GL Pull:</strong> {indicator} {timestamp}</span>;
    }

    return (
        <span>
            <strong>GL Pull:</strong>{' '}
            <Popover
                header="Column pull status"
                position="bottom"
                size="large"
                triggerType="custom"
                dismissButton={false}
                content={(
                    <table className="segmentPullStatusTable">
                        <tbody>
                            {columnIds.map((columnId) => {
                                const columnStatus = configStatuses[columnId];
                                const presentation = PRESENTATION[columnStatus];
                                const error = errors[columnId];
                                return (
                                    <tr key={columnId}>
                                        <td className="pullStatusColumnName">{columnLabel(columnId, columns)}</td>
                                        <td>
                                            {presentation
                                                ? (
                                                    <StatusIndicator type={presentation.type}>
                                                        {presentation.label}
                                                    </StatusIndicator>
                                                )
                                                : <StatusIndicator type="info">{String(columnStatus)}</StatusIndicator>}
                                        </td>
                                        {/* The reason a column failed is the actionable part, so it is shown
                                            inline rather than left in a log. */}
                                        <td className="pullStatusError">{error ?? ''}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            >
                {/* Wrapped so the indicator itself is the hover target and reads as interactive. */}
                <span className="segmentPullStatusTrigger">{indicator}</span>
            </Popover>
            {/* Outside the popover trigger: the finish time is plain information, not something to hover. */}
            {timestamp && <>{' '}{timestamp}</>}
        </span>
    );
}
